package com.dotdrives.web.pages.settings

import com.dotdrives.controller.WorkflowController
import com.dotdrives.controller.WorkflowState
import com.dotdrives.model.*
import com.dotdrives.design.DotTone
import com.dotdrives.web.ui.*
import kotlinx.serialization.json.*
import react.*
import react.dom.html.ReactHTML.div
import web.cssom.ClassName

external interface BillingSubscriptionCardProps : Props {
    var workflow: WorkflowController
    var state: WorkflowState
}

val BillingSubscriptionCard = FC<BillingSubscriptionCardProps> { props ->
    val workflow = props.workflow
    val s = props.state
    val status = s.results["billing-portal:status"]
    val subscription =
        status?.get("subscription")?.takeUnless { it is JsonNull }?.jsonObject
    div {
        className = ClassName("settings-section-card")
        div {
            className = ClassName("settings-section-title")
            +"Subscription"
        }
        div {
            role = "status".unsafeCast<react.dom.aria.AriaRole>()
            +(if (status == null) {
                if (s.readErrors.containsKey("billing-portal:status")) "Subscription could not be loaded. Refresh billing to try again."
                else "Loading subscription…"
            }
            else
                subscription?.let {
                    val trialEnd = it.text("trial_ends_at")
                    val trialExpired =
                        trialEnd?.let { end ->
                            runCatching {
                                    kotlin.time.Instant.parse(end) <=
                                        kotlin.time.Clock.System.now()
                                }
                                .getOrDefault(false)
                        } == true
                    if (it.text("plan") == "trial")
                        "${if (trialExpired) "Trial ended" else "Product trial"}${trialEnd?.let { end -> " · ${if (trialExpired) "ended" else "ends"} ${formatDate(kotlin.time.Instant.parse(end))} UTC" }.orEmpty()}"
                    else {
                        val statusLabel = when (it.text("status")) {
                            "past_due" -> "Payment overdue"
                            "canceled" -> "Subscription ended"
                            "active" -> "Active"
                            "comped" -> "Complimentary"
                            else -> it.text("status").orEmpty().replace('_', ' ').replaceFirstChar(Char::uppercase)
                        }
                        "${it.text("plan").orEmpty().replaceFirstChar(Char::uppercase)} · $statusLabel"
                    }
                } ?: "No subscription on file")
        }
        if (status != null && !status.flag("has_provider_subscription"))
            div { +"No paid subscription is connected." }
        DotButton {
            text = "Refresh billing"
            variant = com.dotdrives.design.DotButtonVariant.Secondary
            onClick = {
                workflow.read("billing-portal")
                workflow.read("billing-portal", "history")
            }
        }
        val canPurchase = status?.flag("can_purchase") == true
        val canChange = status?.flag("can_change_plan") == true
        val available = !s.busy && s.pending == null
        if (status?.flag("can_retry_payment") == true) {
            DotBanner {
                message = "A payment is outstanding. Update your payment method, then open the invoice to retry payment or complete bank verification."
                tone = DotTone.Warning
            }
            DotButton {
                text = "Retry payment"
                enabled = available
                onClick = { workflow.perform("billing-portal", "retry") }
            }
        }
        if (status?.flag("can_reactivate") == true) {
            DotBanner { message = "Your subscription is scheduled to end. Reactivate it to keep your current plan."; tone = DotTone.Warning }
            DotButton {
                text = "Reactivate subscription"
                enabled = available
                onClick = { workflow.perform("billing-portal", "reactivate") }
            }
        }
        if (canPurchase && status?.flag("has_provider_subscription") == true) {
            DotBanner { message = "Your subscription has ended. Choose a plan to restart it with secure checkout."; tone = DotTone.Warning }
        }
        val plans = status?.get("plans")?.jsonArray
        if (plans?.any { !it.jsonObject.flag("available") } == true) div {
            className = ClassName("settings-section-subtitle")
            +"Some plans are not available for online signup yet. Contact Dot Drives support to arrange subscription setup, then refresh billing here."
        }
        if (canPurchase || canChange) plans?.forEach { element ->
            val plan = element.jsonObject
            val name = plan.text("plan") ?: return@forEach
            val displayPlan = name.replaceFirstChar(Char::uppercase)
            DotButton {
                text =
                    if (plan.flag("available"))
                        "${if (canPurchase && status?.flag("has_provider_subscription") == true) "Restart with" else if (canPurchase) "Choose" else "Change to"} $displayPlan"
                    else "$displayPlan · setup required"
                enabled = plan.flag("available") && available
                onClick = {
                    workflow.perform(
                        "billing-portal",
                        if (canPurchase)
                            "purchase"
                        else "change",
                        mapOf("plan" to name),
                    )
                }
            }
        }
        if (status?.flag("payment_management_available") == true)
            DotButton {
                text = "Update payment method"
                enabled = available
                onClick = { workflow.perform("billing-portal", "portal") }
            }
        if (plans?.any { it.jsonObject.flag("available") } == true) div {
            className = ClassName("settings-section-subtitle")
            +"Checkout shows your price before purchase. Stripe confirms billing changes before access updates. An administrator suspension still requires support."
        }
    }
}
